import { useEffect, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Select, DatePicker } from '../../ui';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { Eye } from 'lucide-react';
import type { Dayjs } from 'dayjs';
import { disableFuture } from '../../lib/dateRange';
import { request } from '../../api/client';
import { TwDataTable, Pill, type DataTableColumn } from '../../components/TwDataTable';
import { Button } from '../../ui/Button';
import { Drawer } from '../../ui';
import { formatIst } from '../../lib/time';
import { fmtQty } from '../../lib/format';
import { ReturnDetailPanel } from './ReturnDetailPage';

// Lifecycle status → pill colour.
const STATUS_PILL: Record<string, string> = {
  REQUESTED: 'blue', APPROVED: 'lime', IN_TRANSIT: 'gold', RECEIVED: 'purple',
  CREDITED: 'green', REJECTED: 'red', CANCELLED: 'default', POSTED: 'green',
};
const statusLabel = (s: string) => (s ?? '').replace(/_/g, ' ');
const reasonLabel = (s?: string) => (s ?? '').replace(/_/g, ' ').toLowerCase();

export function ReturnsListPage() {
  const [sp] = useSearchParams();
  const { id: routeId } = useParams(); // deep-link /returns/:id → open the drawer for that return
  const navigate = useNavigate();
  const [status, setStatus] = useState<string | undefined>(sp.get('status') || undefined);
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [openId, setOpenId] = useState<string | null>(null); // right-side detail drawer
  useEffect(() => { setStatus(sp.get('status') || undefined); }, [sp]);
  // A deep-link (notification / activity log) carries the id in the URL → open the drawer on load.
  useEffect(() => { if (routeId) setOpenId(routeId); }, [routeId]);

  // Closing the drawer clears the selection; if we arrived via a deep-link, drop back to the clean /returns
  // URL (replace, so Back doesn't re-open it) so the same link can be followed again later.
  const closeDrawer = () => { setOpenId(null); if (routeId) navigate('/returns', { replace: true }); };

  const { data, isLoading } = useQuery({
    queryKey: ['returns'],
    queryFn: () => request<any[]>({ url: '/returns' }),
    placeholderData: keepPreviousData,
  });
  const all = Array.isArray(data) ? data : [];
  // The list endpoint is a bare (unpaginated) array → filter client-side.
  const from = range[0]?.startOf('day');
  const to = range[1]?.endOf('day');
  const rows = all.filter((r: any) => {
    if (status && r.status !== status) return false;
    // Apply each bound independently so a one-sided range (only from, or only to) still filters.
    if (from || to) {
      const d = new Date(r.createdAt).getTime();
      if (from && d < from.valueOf()) return false;
      if (to && d > to.valueOf()) return false;
    }
    return true;
  });

  const columns: DataTableColumn<any>[] = [
    { key: 'returnNo', title: 'Return No', dataIndex: 'returnNo', sortable: true, alwaysVisible: true, width: 160 },
    { key: 'orderNo', title: 'Order', dataIndex: 'orderNo', sortable: true, width: 150, render: (v: string) => v ?? '—' },
    {
      key: 'retailer', title: 'Retailer', alwaysVisible: true,
      render: (_: any, r: any) => (
        <div>
          <div className="font-semibold text-content">{r.companyName ?? `#${r.retailerId}`}</div>
          {r.retailerCode && <div className="text-xs text-subtle">{r.retailerCode}</div>}
        </div>
      ),
    },
    { key: 'status', title: 'Status', dataIndex: 'status', sortable: true, width: 120, render: (v: string) => <Pill color={STATUS_PILL[v] ?? 'default'}>{statusLabel(v)}</Pill> },
    { key: 'requestedQty', title: 'Qty', dataIndex: 'requestedQty', sortable: true, align: 'right', width: 90, render: (v: any) => fmtQty(v) },
    { key: 'reasonCode', title: 'Reason', dataIndex: 'reasonCode', width: 160, defaultVisible: true, render: (v: string) => <span className="capitalize text-muted">{reasonLabel(v) || '—'}</span> },
    { key: 'createdAt', title: 'Raised', dataIndex: 'createdAt', sortable: true, width: 150, render: (v: string) => formatIst(v) },
    {
      key: 'actions', title: '', width: 80, align: 'right', alwaysVisible: true,
      render: (_: any, r: any) => (
        <Button variant="text" size="sm" icon={<Eye className="size-3.5" />} onClick={(e) => { e.stopPropagation(); setOpenId(String(r.id)); }}>View</Button>
      ),
    },
  ];

  return (
    <div>
      <h1 className="mb-3 text-xl font-semibold text-content">Returns</h1>
      <TwDataTable
        storageKey="returns-list"
        rowKey="id"
        loading={isLoading}
        dataSource={rows}
        columns={columns}
        onRow={(r) => ({ onClick: () => setOpenId(String(r.id)) })}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        emptyText="No returns"
        toolbarLeft={
          <>
            <Select
              placeholder="Filter status" allowClear style={{ width: 190 }}
              value={status} onChange={setStatus}
              options={Object.keys(STATUS_PILL).filter((s) => s !== 'POSTED').map((s) => ({ value: s, label: statusLabel(s) }))}
            />
            <DatePicker.RangePicker disabledDate={disableFuture} value={range as any} onChange={(r) => setRange((r as any) ?? [null, null])} allowEmpty={[true, true]} />
          </>
        }
      />

      {/* Row click opens the detail in a right-side drawer (no page nav). Approve/reject/withdraw from within
          it refresh the list; a terminal action (reject/withdraw) closes the drawer. */}
      <Drawer
        title="Return details"
        width={620}
        open={!!openId}
        onClose={closeDrawer}
      >
        {openId && <ReturnDetailPanel id={openId} onClosed={closeDrawer} />}
      </Drawer>
    </div>
  );
}
